import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { collection, getDocs, doc, deleteDoc, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import toast from 'react-hot-toast';


const AdminDeleteRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchDeleteRequests();
  }, []);

  const fetchDeleteRequests = async () => {
    try {
      setLoading(true);
      const q = query(
        collection(db, 'deleteRequests'),
        where('status', '==', 'pending'),
        orderBy('requestedAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const requestsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setRequests(requestsData);
    } catch (error) {
      console.error('Error fetching delete requests:', error);
      toast.error('Failed to fetch deletion requests');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUserData = async (request) => {
    setIsDeleting(true);
    try {
      const userId = request.userId;

      // Delete user document from users collection
      try {
        await deleteDoc(doc(db, 'users', userId));
      } catch (err) {
        console.warn('User document not found or already deleted:', err);
      }

      // Delete or anonymize orders (keep for business records but remove PII)
      const ordersQuery = query(collection(db, 'orders'), where('userId', '==', userId));
      const ordersSnapshot = await getDocs(ordersQuery);
      for (const orderDoc of ordersSnapshot.docs) {
        await deleteDoc(doc(db, 'orders', orderDoc.id));
        // Alternative: anonymize instead of delete
        // await updateDoc(doc(db, 'orders', orderDoc.id), {
        //   customerEmail: '[deleted]',
        //   shippingAddress: { name: '[deleted]', phone: '[deleted]', email: '[deleted]', address: '[deleted]' }
        // });
      }

      // Delete cart items
      const cartQuery = query(collection(db, 'carts'), where('userId', '==', userId));
      const cartSnapshot = await getDocs(cartQuery);
      for (const cartDoc of cartSnapshot.docs) {
        await deleteDoc(doc(db, 'carts', cartDoc.id));
      }

      // Delete the deletion request itself
      await deleteDoc(doc(db, 'deleteRequests', request.id));

      // Note: Firebase Auth user deletion requires server-side or user re-authentication
      // For now, we'll just delete Firestore data. Add Cloud Function for Auth deletion.

      toast.success(`User data deleted successfully for ${request.userEmail}`);
      setDeleteModal(null);
      fetchDeleteRequests();
    } catch (error) {
      console.error('Error deleting user data:', error);
      toast.error('Failed to delete user data. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRejectRequest = async (requestId) => {
    try {
      await deleteDoc(doc(db, 'deleteRequests', requestId));
      toast.success('Deletion request rejected');
      fetchDeleteRequests();
    } catch (error) {
      console.error('Error rejecting request:', error);
      toast.error('Failed to reject request');
    }
  };

  return (
    <div className="pb-8 md:pb-16">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-serif text-deep-brown">User Deletion Requests</h1>
          <p className="text-sm md:text-base text-warm-gray font-medium">Manage data deletion requests</p>
        </div>



        {/* Main Content */}
        {loading ? (
          <div className="flex items-center justify-center h-64 mt-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold"></div>
          </div>
        ) : requests.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-warm-gray/20 p-12 text-center mt-8">
            <p className="text-warm-gray text-lg">No pending deletion requests</p>
          </div>
        ) : (
          <div className="space-y-4 mt-8">
            {requests.map((request) => (
              <motion.div
                key={request.id}
                className="bg-white rounded-lg shadow-sm border border-warm-gray/20 p-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-lg font-semibold text-deep-brown">
                          {request.userName}
                        </h3>
                        <p className="text-sm text-warm-gray">{request.userEmail}</p>
                        {request.userPhone && (
                          <p className="text-sm text-warm-gray">{request.userPhone}</p>
                        )}
                      </div>
                      <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
                        Pending
                      </span>
                    </div>

                    <div className="mb-4">
                      <p className="text-sm font-medium text-deep-brown mb-1">Reason:</p>
                      <p className="text-sm text-warm-gray bg-cream/50 p-3 rounded">
                        {request.reason || 'No reason provided'}
                      </p>
                    </div>

                    <p className="text-xs text-warm-gray">
                      Requested on:{' '}
                      {request.requestedAt?.toDate?.()
                        ? new Date(request.requestedAt.toDate()).toLocaleString('en-IN')
                        : 'N/A'}
                    </p>
                  </div>

                  <div className="flex lg:flex-col gap-3">
                    <button
                      onClick={() => setDeleteModal(request)}
                      className="flex-1 lg:flex-initial px-6 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors duration-300 font-medium"
                    >
                      Delete User Data
                    </button>
                    <button
                      onClick={() => handleRejectRequest(request.id)}
                      className="flex-1 lg:flex-initial px-6 py-2 border border-warm-gray/30 text-deep-brown rounded-md hover:bg-gray-50 transition-colors duration-300"
                    >
                      Reject
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Confirmation Modal */}
        <AnimatePresence>
          {deleteModal && (
            <motion.div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
              >
                <div className="mb-4">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                    <svg
                      className="h-6 w-6 text-red-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-serif text-center text-deep-brown mb-2">
                    Confirm User Data Deletion
                  </h3>
                  <p className="text-sm text-warm-gray text-center mb-4">
                    Are you sure you want to permanently delete all data for{' '}
                    <strong className="text-deep-brown">{deleteModal.userName}</strong> (
                    {deleteModal.userEmail})?
                  </p>
                  <div className="bg-red-50 border-l-4 border-red-400 p-3 mb-4">
                    <p className="text-xs text-red-800">
                      <strong>Warning:</strong> This action will permanently delete:
                    </p>
                    <ul className="text-xs text-red-700 mt-2 space-y-1 ml-4">
                      <li>• User profile and account data</li>
                      <li>• Order history</li>
                      <li>• Cart items</li>
                      <li>• This deletion request</li>
                    </ul>
                    <p className="text-xs text-red-800 mt-2">
                      This action <strong>cannot be undone</strong>.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setDeleteModal(null)}
                    disabled={isDeleting}
                    className="flex-1 px-4 py-2 border border-warm-gray/30 text-deep-brown rounded-md hover:bg-gray-50 transition-colors duration-300 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleDeleteUserData(deleteModal)}
                    disabled={isDeleting}
                    className="flex-1 px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors duration-300 disabled:opacity-50 font-medium"
                  >
                    {isDeleting ? 'Deleting...' : 'Yes, Delete All Data'}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default AdminDeleteRequests;
